<template>
    <div :class="classList">
        <input
            :id="inputConfig.id"
            :name="inputConfig.name"
            :class="inputConfig.styleClass"
            :type="inputConfig.type"
            :maxlength="inputConfig.maxlength"
        />
        <label :class="{ floated: floatLabel }" :for="inputConfig.id">{{
            inputConfig.label
        }}</label>
    </div>
</template>

<script>
    import insertScript from 'helpers/insertScript';

    export default {
        name: 'LabelInputSheerID',
        props: {
            inputConfig: {
                type: Object,
                required: true
            },
            errorMessage: {
                type: String,
                required: false,
                default: ''
            }
        },
        data: function () {
            return {
                focused: false,
                hasInput: false,
                organizationID: null,
                organizationName: null,
                sheerIDInput: null,
                sheerIDScriptTag: null,
                sheerIDWrapperMutationObserver: null,
                sheerIDInputListeners: {
                    input: null,
                    blur: null,
                    focus: null
                }
            };
        },

        computed: {
            floatLabel() {
                return this.focused || this.hasInput;
            },

            classList: function () {
                let list = ['field-wrapper field-wrapper--floating-label'];

                if (typeof this.inputConfig.name === 'string') {
                    list.push(`field-wrapper--${this.inputConfig.name}`);
                }

                if (this.focused) list.push('focused');
                if (this.errorMessage) list.push('error');

                return list;
            }
        },

        methods: {
            handleFocus() {
                this.focused = true;

                this.$emit('focus', {
                    name: this.inputConfig.name,
                    value: this.organizationName
                });
            },

            handleBlur() {
                this.focused = false;

                this.$emit('blur', {
                    value: this.organizationName
                });
            },

            addSheerIdInputListeners() {
                this.sheerIDInputListeners.input = this.sheerIDInput.addEventListener(
                    'input',
                    (e) => {
                        this.hasInput =
                            typeof e.target.value === 'string' && e.target.value.trim().length > 0;
                    }
                );

                this.sheerIDInputListeners.focus = this.sheerIDInput.addEventListener(
                    'focus',
                    (e) => {
                        this.handleFocus();
                    }
                );

                this.sheerIDInputListeners.blur = this.sheerIDInput.addEventListener(
                    'blur',
                    (e) => {
                        this.hasInput =
                            typeof e.target.value === 'string' && e.target.value.trim().length > 0;
                        this.handleBlur();
                    }
                );
            },

            removeSheerIDInputListeners() {
                Object.keys(this.sheerIDInputListeners).forEach((key) => {
                    this.sheerIDInput.removeEventListener(key, this.sheerIDInputListeners[key]);
                });
            },

            loadSheerID() {
                this.sheerIDWrapperMutationObserver = new MutationObserver(
                    (mutationList, observer) => {
                        mutationList.forEach((mutation) => {
                            switch (mutation.type) {
                                case 'childList':
                                    this.sheerIDInput = document.querySelector('input[id="name"]');

                                    if (this.sheerIDInput) {
                                        this.sheerIDInput.setAttribute(
                                            'tabindex',
                                            this.inputConfig.tabIndex
                                        );
                                        this.addSheerIdInputListeners();
                                        this.sheerIDWrapperMutationObserver.disconnect();
                                    }

                                    break;
                                default:
                                    break;
                            }
                        });
                    }
                );

                this.sheerIDWrapperMutationObserver.observe(
                    document.querySelector('.field-wrapper--university'),
                    { childList: true }
                );

                let context = this;

                SheerID.load('combobox', '2.0', {
                    config: {
                        input: document.querySelector('input[name="university"]'),
                        allowName: false,
                        proxyName: 'name',
                        listeners: {
                            change: (orgEntity) => {
                                if (orgEntity.id) {
                                    this.hasInput = true;
                                    context.$emit('input', {
                                        name: 'name',
                                        value: orgEntity.name
                                    });
                                    context.$emit('input', {
                                        name: 'university',
                                        value: orgEntity.id
                                    });
                                } else {
                                    this.hasInput = false;
                                    context.$emit('input', {
                                        name: 'name',
                                        value: null
                                    });

                                    context.$emit('input', {
                                        name: 'university',
                                        value: null
                                    });
                                }
                            }
                        },
                        params: {
                            type: ['UNIVERSITY']
                        }
                    }
                });
            }
        },

        // Lifecycle Hooks
        mounted() {
            this.$nextTick(() => {
                insertScript('https://services.sheerid.com/jsapi/SheerID.js', true)
                    .then((tag) => {
                        this.sheerIDScriptTag = tag;
                        this.loadSheerID();
                    })
                    .catch((err) => {
                        console.error(err);
                    });
            });
        },

        beforeDestroy() {
            this.sheerIDScriptTag.parentNode.removeChild(this.sheerIDScriptTag);
            this.removeSheerIDInputListeners();
            this.sheerIDWrapperMutationObserver.disconnect();
        }
    };
</script>
